import React, { useState } from "react";
import * as XLSX from "xlsx-js-style";
import { useGetEventAttendees } from "../../hooks/useAttendee";
import { useCountryNameById } from "../../hooks/useCountry";
import templateFile from "../../assets/LOA-template.xlsx";
import { useGetEventDataById } from "../../hooks/useEvent";
import { useCityNameById } from "../../hooks/useCity";
import { formatDate, formatPrice, formatTime } from "../../utils/helpers";
import countryService from "../../services/countryService";

function AttendeesReportAttendEvent({ eventId }) {
  const { data: attendeesData } = useGetEventAttendees(eventId);
  const { data: eventData } = useGetEventDataById(eventId);
  const { data: cityName } = useCityNameById(eventData?.city_id);
  const { data: countryName } = useCountryNameById(eventData?.country_id);
  const [isDownloading, setIsDownloading] = useState(false);
  const countryNames = {};

  const calculateSpendPerAttendee = () => {
    if (!eventData?.spend_value || !attendeesData?.length) return 0;
    const totalSpend = parseFloat(eventData.spend_value);
    const attendeeCount = attendeesData.length;
    const spendPerAttendee = totalSpend / attendeeCount;
    return formatPrice(spendPerAttendee);
  };

  const getCached = async (cache, service, id) => {
    if (!id) return "";
    if (!cache[id]) cache[id] = await service.getNameById(id);
    return cache[id];
  };

  const handleExport = async () => {
    try {
      setIsDownloading(true);
      const spendValue = calculateSpendPerAttendee();
      // Read the template file
      const response = await fetch(templateFile);
      const arrayBuffer = await response.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, {
        type: "array",
        bookImages: true,
      });

      // Select the first worksheet (assuming your template is in the first sheet)
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];

      const columnWidths = [
        { wch: 25 }, // Column A
        { wch: 25 }, // Column B
        { wch: 40 }, // Column C
        { wch: 45 }, // Column D
        { wch: 40 }, // Column E
        { wch: 30 }, // Column F
        { wch: 50 }, // Column G
        { wch: 30 }, // Column H
        { wch: 45 }, // Column I
        { wch: 25 }, // Column J
        { wch: 25 }, // Column K
        { wch: 25 }, // Column L
        { wch: 25 }, // Column M
      ];

      worksheet["!cols"] = columnWidths;

      worksheet["B5"] = {
        v:
          `${formatDate(eventData?.start_date)} - ${formatDate(
            eventData?.end_date
          )}` || "",
        t: "s",
        s: { font: { bold: true, sz: 12 }, alignment: { horizontal: "left" } },
      };

      worksheet["B6"] = {
        v:
          `${formatTime(eventData?.start_time)} - ${formatTime(
            eventData?.end_time
          )}` || "",
        t: "s",
        s: { font: { bold: true, sz: 12 }, alignment: { horizontal: "left" } },
      };
      worksheet["B8"] = {
        v: `${countryName || ""}, ${cityName || ""}`,
        t: "s",
        s: { font: { bold: true, sz: 12 }, alignment: { horizontal: "left" } },
      };

      worksheet["E9"] = {
        v: eventData?.name || "",
        t: "s",
        s: { font: { bold: true, sz: 12 }, alignment: { horizontal: "left" } },
      };
      worksheet["H9"] = {
        v: formatPrice(eventData?.spend_value) || "",
        t: "s",
        s: { font: { bold: true, sz: 12 }, alignment: { horizontal: "left" } },
      };

      const formatAttendeesData = async () => {
        const formattedData = await Promise.all(
          attendeesData.map(async (attendee) => {
            // Wait for country name to resolve
            const attendeeCountryName = await getCached(
              countryNames,
              countryService,
              attendee.country_id
            );

            return [
              attendee.APO_number,
              attendee.first_name,
              attendee.family_name,
              attendee.specialty,
              attendee.medical_license,
              attendee.national_id,
              attendee.email,
              attendee.phone_number,
              attendee.industry_area || "",
              attendee.organization,
              spendValue,
              attendeeCountryName, // ✅ now it's the actual name, not a Promise
              attendee.signature && attendee.signature !== "No Signature"
                ? {
                    v: "View Signature",
                    l: { Target: attendee.signature },
                    t: "s",
                    s: {
                      font: { color: { rgb: "0000FF" } },
                    },
                  }
                : attendee.signature || "",
            ];
          })
        );

        return formattedData;
      };

      const data = await formatAttendeesData();

      const dataEndRow = 12 + data.length; // 12 is the starting row index (A13)

      // If data extends beyond row 20, move content from A21-A31 down
      if (dataEndRow >= 20) {
        // Store content from rows 21-31 for all columns A-L
        const rowsContent = {};
        for (let i = 21; i <= 31; i++) {
          rowsContent[i] = {};
          for (let col = "A".charCodeAt(0); col <= "L".charCodeAt(0); col++) {
            const colLetter = String.fromCharCode(col);
            const cellRef = `${colLetter}${i}`;
            if (worksheet[cellRef]) {
              rowsContent[i][colLetter] = worksheet[cellRef];
              delete worksheet[cellRef];
            }
          }
        }

        // Add the content after the data
        const newStartRow = dataEndRow + 1;
        Object.keys(rowsContent).forEach((oldRow, index) => {
          const newRow = newStartRow + index;
          for (let col = "A".charCodeAt(0); col <= "L".charCodeAt(0); col++) {
            const colLetter = String.fromCharCode(col);
            if (rowsContent[oldRow][colLetter]) {
              worksheet[`${colLetter}${newRow}`] = {
                v: rowsContent[oldRow][colLetter].v,
                t: "s",
                s: {
                  font: { bold: true, sz: 12 },
                  alignment: { horizontal: "left" },
                },
              };
            }
          }
        });
      }
      // Add headers and data with center alignment
      const formattedData = data.map((row) =>
        row.map((cell) => {
          if (typeof cell === "object" && cell.l) {
            // Preserve hyperlink formatting while adding center alignment
            return {
              ...cell,
              s: {
                ...cell.s,
                alignment: { horizontal: "center" },
              },
            };
          }
          return {
            v: cell,
            t: "s",
            s: {
              alignment: { horizontal: "center" },
            },
          };
        })
      );

      XLSX.utils.sheet_add_aoa(worksheet, formattedData, { origin: "A13" });

      // Make row 12 bold (headers)
      for (let col = "A".charCodeAt(0); col <= "M".charCodeAt(0); col++) {
        const cellRef = `${String.fromCharCode(col)}12`;
        if (worksheet[cellRef]) {
          worksheet[cellRef].s = {
            font: { bold: true, sz: 12 },
            alignment: { horizontal: "center" },
          };
        }
      }

      // Generate the file name
      const fileName = `${eventData.name}_attendees.xlsx`;

      // Write the updated workbook
      XLSX.writeFile(workbook, fileName);
    } catch (error) {
      console.error("Error exporting file:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <button
      data-track="export-attendees-button"
      data-module="Attendees"
      onClick={handleExport}
      disabled={isDownloading}
      className={`bg-emerald-600 hover:bg-emerald-700 transition-colors duration-200 text-white rounded-md px-4 py-2 flex items-center gap-2 ${
        isDownloading ? "opacity-80 cursor-not-allowed" : ""
      }`}
    >
      {isDownloading ? (
        <i className="fa-solid fa-spinner fa-spin text-white animate-spin"></i> 
      ) : (
        <i className="fa-solid fa-file-export"></i>
      )}
      {isDownloading ? "Exporting..." : "LOA Report"}
    </button>
  );
}

export default AttendeesReportAttendEvent;


